﻿import { Kb3dModel } from '../decorators/kb3d-decorator';
import { IUvData } from '../kb3d-models';
import { MeshNode } from '../meshes/mesh-node';
import { Token } from '../token';

export const NEW_GEOMETRY = 'NEW_GEOMETRY';

/** IUvData has number[] props needed for server, but we want
 * them to be float32array here
 */
export type UvBuffer = {
    [P in keyof IUvData]: Float32Array;
};

export interface KBVertexData extends UvBuffer {
    indices?: Uint32Array;
    positions?: Float32Array;
    normals?: Float32Array;
    tangents?: Float32Array;
}

const MAX_INT16 = 32767;
const MAX_UINT16 = 65535;

@Kb3dModel({
    token: Token.KbGeometry,
    trackChanges: false,
})
export class KbGeometry implements KBVertexData {
    indices?: Uint32Array;
    positions?: Float32Array;
    normals?: Float32Array;
    tangents?: Float32Array;
    uvs?: Float32Array;
    /**
     * This property is used by the mesh renderer when a node has a pretransform (baked transform) defined.
     * The first time the geometry is transformed, it's mutated. The 4x4 transform matrix is stored here.
     * When this geometry is used in the future, this transform has to be taken into account for any future transforms (or lack of a transform)
     * This is so that we can reuse a transformed geometry if many nodes have the same transform or if a single node needs to rebuild
     * it's own geometry often, which is the most common use case.
     */
    _hasTransform?: number[];

    shared = false;

    /**
     * A geometry object. Geometries are immutable, once they are created they should not be modified.
     */
    constructor() {}

    clone() {
        const newGeometry = new KbGeometry();
        newGeometry.indices = this.indices;
        newGeometry.positions = this.positions;
        newGeometry.normals = this.normals;
        newGeometry.tangents = this.tangents;
        newGeometry.uvs = this.uvs;

        return newGeometry;
    }

    /**
     * Freeze a geometry to enforce the immutability of geometries
     */
    // freeze() {
    //     Object.freeze(this);
    //     Object.freeze(this.vertexData);
    // }

    // update(vertexData: Partial<KBVertexData & IUvData>) {
    //     const newVertexData = { ...this.vertexData, ...vertexData };
    //     const newGeometry = new KbGeometry(newVertexData, this.id);
    //     return newGeometry;
    // }

    extractFromBinary(fileData: ArrayBuffer, meshNode: MeshNode): void {
        const dataView = new DataView(fileData);
        let curPos: number;

        // indices
        const indicesOffset = dataView.getInt32(0, true);
        curPos = 4;
        const indicesDataSize = dataView.getInt32(curPos, true);
        curPos += 4;

        let indices: Uint32Array;
        if (indicesDataSize === 16) {
            const indices16 = new Uint16Array(fileData, curPos, indicesOffset / 2);
            indices = Uint32Array.from(indices16);
            curPos += indicesOffset;
        } else if (indicesDataSize === 32) {
            indices = new Uint32Array(fileData, curPos, indicesOffset / 4);
            curPos += indicesOffset;
        } else {
            throw Error('Unknown indices data size ' + meshNode.name);
        }

        // positions
        const posOffset = dataView.getInt32(curPos, true);
        curPos += 4;
        const posDataSize = dataView.getInt32(curPos, true);
        curPos += 4;
        const posRatio = dataView.getInt32(curPos, true);
        curPos += 4;

        let positions: Float32Array;
        if (posDataSize === 32) {
            positions = new Float32Array(fileData.slice(curPos, curPos + posOffset));
            for (let i = 0; i < positions.length; i++) {
                positions[i] /= posRatio;
            }
            curPos += posOffset;
        } else {
            throw Error('Invalid positions in binary ' + meshNode.name);
        }

        // normals
        const norOffset = dataView.getInt32(curPos, true);
        curPos += 4;
        const norDataSize = dataView.getInt32(curPos, true);
        curPos += 4;
        const norRatio = dataView.getInt32(curPos, true);
        curPos += 4;

        let normals: Float32Array;
        if (norDataSize === 16) {
            const normalsInt = new Int16Array(fileData.slice(curPos, curPos + norOffset));
            normals = new Float32Array(normalsInt.length);
            for (let i = 0; i < normalsInt.length; i++) {
                normals[i] = normalsInt[i] / norRatio;
            }
            curPos += norOffset;
        } else {
            throw Error('Invalid normals in binary ' + meshNode.name);
        }

        const uvsOffset = dataView.getInt32(curPos, true);
        curPos += 4;
        const uvsDataSize = dataView.getInt32(curPos, true);
        curPos += 4;
        const uvsRatio = dataView.getInt32(curPos, true);
        curPos += 4;

        let uvs: Float32Array;
        if (uvsDataSize === 16) {
            const uvsInt = new Int16Array(fileData.slice(curPos, curPos + uvsOffset));
            uvs = new Float32Array(uvsInt.length);
            for (let i = 0; i < uvsInt.length; i++) {
                uvs[i] = uvsInt[i] / uvsRatio;
            }
            curPos += uvsOffset;
        } else {
            throw Error('Invalid uvs in binary ' + meshNode.name);
        }

        // Error reporting
        if (indices.length % 3 > 0) {
            console.warn('Incomplete indices triangles in mesh ' + meshNode.name);
        }
        if (positions.length % 3 > 0) {
            console.warn('Incomplete position vectors in mesh ' + meshNode.name);
        }
        if (normals.length % 3 > 0) {
            console.warn('Incomplete normal vectors in mesh ' + meshNode.name);
        }
        if (uvs.length % 2 > 0) {
            console.warn('Incomplete uv vectors in mesh ' + meshNode.name);
        }
        if (uvs.length > 0 && positions.length !== normals.length) {
            console.warn('Normals and Positions array size mismatch in ' + meshNode.name);
        }
        if (uvs.length > 0 && positions.length / 3 !== uvs.length / 2) {
            console.warn('UV array size mismatch in ' + meshNode.name);
        }
        // End error reporting

        this.positions = positions;
        this.indices = indices;
        if (normals.length > 0) {
            this.normals = normals;
        }
        if (uvs.length > 0) {
            this.uvs = uvs;
        }
    }

    toBinary(): ArrayBuffer | undefined {
        if (!this.positions || !this.indices) {
            return;
        }
        const positions = this.positions;
        const indices = this.indices;
        const normals = this.normals || new Float32Array([]);
        const uvs = this.uvs || new Float32Array([]);

        let fileLen = 0;
        let indicesOffset = 8; // indices data markers
        let indicesBits = 0;
        let indicesLength = 0;
        let posOffset = 0;
        let posBits = 0;
        let posLength = 0;
        let normalsOffset = 0;
        let normalsBits = 0;
        let normalsLength = 0;
        let uvsOffset = 0;
        let uvsBits = 0;
        let uvsLength = 0;
        // calculate the total size of the array

        fileLen += indicesOffset;
        if (canFitInUInt16Bit(indices)) {
            // write a 16bit array
            indicesBits = 16;
            indicesLength = indices.length * 2;
            fileLen += indicesLength;
        } else {
            // write a 32bit array
            indicesBits = 32;
            indicesLength = indices.length * 4;
            fileLen += indicesLength;
        }

        fileLen += 12; // position data markers
        posOffset = fileLen;
        posBits = 32;
        fileLen += positions.length * 4;
        posLength = positions.length * 4;

        fileLen += 12; // normals data markers
        normalsOffset = fileLen;
        // write the normals 16 bit array
        normalsBits = 16;
        fileLen += normals.length * 2;
        normalsLength = normals.length * 2;

        fileLen += 12; // uv data markers
        uvsOffset = fileLen;
        uvsBits = 16;
        fileLen += uvs.length * 2;
        uvsLength = uvs.length * 2;

        // create the buffer that represent the binary file
        const buffer = new ArrayBuffer(fileLen);
        const bufferView = new DataView(buffer);

        // let's write the data into the bufferView
        // write the indices
        bufferView.setInt32(indicesOffset - 8, indicesLength, true); // NOTICE that by default JS is big endian, but since our servers are x86 we are little endian
        bufferView.setInt32(indicesOffset - 4, indicesBits, true);

        if (indicesBits == 16) {
            for (let i = 0; i < indices.length; i++) {
                bufferView.setUint16(indicesOffset, indices[i], true);
                indicesOffset += 2;
            }
        } else if (indicesBits == 32) {
            for (let i = 0; i < indices.length; i++) {
                bufferView.setUint32(indicesOffset, indices[i], true);
                indicesOffset += 4;
            }
        }

        // write the positions
        bufferView.setInt32(posOffset - 12, posLength, true);
        bufferView.setInt32(posOffset - 8, posBits, true);
        bufferView.setInt32(posOffset - 4, 1, true); // ratio

        if (posBits == 32) {
            for (let i = 0; i < positions.length; i++) {
                bufferView.setFloat32(posOffset, positions[i], true);
                posOffset += 4;
            }
        } else {
            console.error('Fix me - non-32bit positions not supported');
        }

        // write the normals
        bufferView.setInt32(normalsOffset - 12, normalsLength, true);
        bufferView.setInt32(normalsOffset - 8, normalsBits, true);
        bufferView.setInt32(normalsOffset - 4, MAX_INT16, true);

        if (normalsBits == 16) {
            for (let i = 0; i < normals.length; i++) {
                const n = floatToIntStore(normals[i], MAX_INT16);
                bufferView.setInt16(normalsOffset, n, true);
                normalsOffset += 2;
            }
        } else {
            console.error('Fix me - non-16bit normals not supported');
        }

        // write the UVs
        bufferView.setInt32(uvsOffset - 12, uvsLength, true);
        bufferView.setInt32(uvsOffset - 8, uvsBits, true);
        bufferView.setInt32(uvsOffset - 4, MAX_INT16, true);

        if (uvsBits == 16) {
            for (let i = 0; i < uvs.length; i++) {
                const n = floatToIntStore(uvs[i], MAX_INT16);
                bufferView.setInt16(uvsOffset, n, true);
                uvsOffset += 2;
            }
        } else {
            console.error('Fix me - non-16bit uvs not supported');
        }

        return buffer;
    }
}

// function canFitInInt16Bit(inArray: Float32Array): boolean {

//     for (let i = 0; i < inArray.length; i++) {
//         const val = inArray[i];
//         if (val > 327.68 || val < -327.68) {
//             return false;
//         }
//     }
//     return true;
// }

// function canFitInInt8Bit(inArray: Float32Array): boolean {
//     for (let i = 0; i < inArray.length; i++) {
//         const val = inArray[i];
//         if (val > 1.28 || val < -1.28) {
//             return false;
//         }
//     }
//     return true;
// }

function canFitInUInt16Bit(inArray: Int32Array | Uint32Array) {
    for (let i = 0; i < inArray.length; i++) {
        const val = inArray[i];
        if (val > MAX_UINT16) {
            return false;
        }
    }
    return true;
}

function floatToIntStore(float: number, maxInt: number, maxVal = 1): number {
    // this function convert a value between -327.68 and 327.68 to an int 16. we use the convention to have only 2 decimal numbers
    float = (Math.min(Math.max(-maxVal, float), maxVal) * maxInt) / maxVal;

    const outVal = Math.trunc(float);
    return outVal;
}
